<template>
  <div>
    <div class="d-flex flex-row align-center justify-space-between">
      <div class="d-flex flex-row align-center">
        <h3 class="mr-4">{{ $t("message.permissions.users") }}</h3>
        <v-tooltip bottom :max-width="500">
          <template v-slot:activator="{ on }">
            <div v-on="on">
              <v-icon color="lime" size="18">
                mdi-information
              </v-icon>
            </div>
          </template>
          {{ $t("message.permissions.usersTooltip") }}
        </v-tooltip>
        <ui-button
          outlined
          color="accent"
          class="owner-btn"
          readonly
          v-if="company.company_user.type === accountTypes.OWNER"
        >
          {{ $t("message.permissions.youAreOwner") }}
        </ui-button>
        <ui-button
          outlined
          color="primary"
          class="owner-btn"
          readonly
          v-else-if="company.company_user.type === accountTypes.ADMIN"
        >
          {{ $t("message.permissions.youAreAdmin") }}
        </ui-button>
        <ui-button outlined class="owner-btn" readonly v-else>
          {{ $t("message.permissions.youAreManager") }}
        </ui-button>
      </div>
      <Pagination
        v-if="count"
        :page="page"
        :per-page="perPage"
        :options="pageOptions"
        :total-count="count"
        @change="onPagination"
      ></Pagination>
    </div>
    <div class="table-container">
      <v-data-table
        v-if="count"
        class="members-list"
        :headers="headers"
        :items="members"
        :page.sync="page"
        :items-per-page="perPage"
        item-key="id"
        hide-default-footer
      >
        <template #item.type="{ item }">
          <div class="type-cell">
            <div
              class="cell-border-left"
              :style="getCellBorderStyle(item.type)"
            ></div>
            <div class="d-flex flex-column">
              <span class="d-block mb-2">
                {{ $t(`message.permissions.accountTypes.${item.type}`) }}
              </span>
              <span class="d-block muted">
                {{ item.email }}
              </span>
            </div>
          </div>
        </template>
        <template #item.invitationAccepted="{ item }">
          <div v-if="item.invitationAccepted || item.isActive">
            <v-icon class="mr-1" color="primary" :size="20">
              icon-status-confirmed
            </v-icon>
            {{ $t("message.permissions.invitationStatus.active") }}
          </div>
          <div v-else>
            <div>
              <v-icon class="mr-1" color="pink" :size="20">
                icon-status-pending
              </v-icon>
              {{ $t("message.permissions.invitationStatus.pending") }}
            </div>
            <ui-button
              text
              class="link-btn resend-btn"
              @click="sendInvitation(item.id)"
              v-if="canManageTeam"
            >
              {{ $t("message.permissions.resend") }}
            </ui-button>
          </div>
        </template>
        <template #item.allFacilitiesAccess="{ item }">
          <div class="d-flex flex-column align-start">
            <span
              v-if="
                item.allFacilitiesAccess || item.type === accountTypes.OWNER
              "
              class="permissions-label"
            >
              {{ $t("message.permissions.all") }}
            </span>
            <span v-else class="permissions-label">
              {{
                item.facilitiesGroupsAccessCount
                  ? getFacilitiesGroupNumberLabel(
                      item.facilitiesGroupsAccessCount,
                      item.facilitiesAccessCount.owner +
                        item.facilitiesAccessCount.not_owner
                    )
                  : getFacilitiesNumberLabel(
                      item.facilitiesAccessCount.owner +
                        item.facilitiesAccessCount.not_owner
                    )
              }}
            </span>
            <ui-button
              text
              class="link-btn see-permissions-btn"
              @click="previewMember(item.id)"
              v-if="canManageTeam"
            >
              {{ $t("message.permissions.see") }}
            </ui-button>
          </div>
        </template>
        <template #item.actions="{ item, value }">
          <div class="actions" v-if="canManageTeam">
            <ui-button
              v-if="item.type !== accountTypes.OWNER"
              transparent
              icon="icon-edit"
              @click="editMember(item.id)"
            ></ui-button>
            <v-menu
              content-class="btn-action-menu"
              transition="slide-x-reverse-transition"
              left
              v-if="item.type !== accountTypes.OWNER"
            >
              <template v-slot:activator="{ on, attrs }">
                <ui-button
                  transparent
                  icon="icon-more"
                  v-bind="attrs"
                  v-on="on"
                ></ui-button>
              </template>

              <ui-button
                icon="icon-trash"
                icon-color="pink"
                @click="deleteMember(item)"
              >
                {{ $t("message.common.delete") }}
              </ui-button>
            </v-menu>
          </div>
        </template>
      </v-data-table>
      <SectionLoader :status="loading" />
    </div>

    <Pagination
      v-if="count"
      :page="page"
      :per-page="perPage"
      :options="pageOptions"
      :total-count="count"
      :show-options="false"
      @change="onPagination"
    ></Pagination>

    <div class="no-members" v-if="!count">
      <h3>{{ $t("message.permissions.noMembers") }}</h3>
      <ui-button
        icon="icon-plus"
        color="primary"
        @click="addHandler"
        v-if="canManageTeam"
      >
        {{ $t("message.permissions.addNewUser") }}
      </ui-button>
      <SectionLoader :status="loading" :opacity="1" />
    </div>
  </div>
</template>

<script>
import Pagination from "@/components/List/Pagination";
import SectionLoader from "@/components/Loader/SectionLoader";
import AlertDialog from "@/components/ui/AlertDialog";

import serviceHandler from "@/helpers/handlers/service";
import { DialogHelper } from "@/helpers/dialog-helper";
import { pluralCount } from "@/helpers/helpers";
import { permissionsInfo } from "@/helpers/mixins/permissions-info";

import { PermissionsActiveDialog } from "@/enums/permissions-active-dialog";
import { AccountType } from "@/enums/account-type";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";

import { CompanyService } from "@/services/permissions";
import usersService from "@/services/users";
import { APP_COLORS } from "@/config/app-colors";

import { mapState } from "vuex";

export default {
  name: "CompanyMembersList",
  components: {
    Pagination,
    SectionLoader
  },
  mixins: [permissionsInfo],
  props: {
    addHandler: {
      type: Function,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      headers: [
        {
          text: `${this.$t("message.permissions.accountType")} / ${this.$t(
            "message.permissions.email"
          )}`,
          value: "type",
          sortable: false
        },
        {
          text: this.$t("message.permissions.teamInvitation"),
          value: "invitationAccepted",
          sortable: false
        },
        {
          text: this.$t("message.permissions.permissions"),
          value: "allFacilitiesAccess",
          sortable: false
        },
        {
          text: "",
          value: "actions",
          sortable: false,
          width: 136
        }
      ],
      members: [],
      count: 0,
      page: 1,
      perPage: 10,
      pageOptions: [10, 25, 50, 100, 200],
      colors: APP_COLORS,
      accountTypes: AccountType
    };
  },
  computed: {
    membersParams() {
      return {
        companyId: this.company.id,
        page: this.page,
        perPage: this.perPage
      };
    },
    ...mapState({
      company: state => state.company.company,
      selectedMember: state => state.permissions.member
    })
  },
  created() {
    this.getMembers();
  },
  methods: {
    async getMembers() {
      this.loading = true;
      const data = await serviceHandler(
        CompanyService.getMembers(this.membersParams)
      );

      if (data && data.count) {
        this.members = data.results;
        this.count = data.count;
        this.$emit("count", this.count);
      }
      this.loading = false;
    },

    async setMember(memberId) {
      this.loading = true;
      const member = await serviceHandler(
        CompanyService.getMember(memberId, this.company.id)
      );

      if (member) {
        this.$store.commit("permissions/RESET_STORE");
        this.$store.commit("permissions/SET_MEMBER", member);
      }

      this.loading = false;
    },

    async editMember(memberId) {
      await this.setMember(memberId);
      if (this.selectedMember && this.selectedMember.id === memberId) {
        this.$store.dispatch("permissions/setActiveDialog", {
          type:
            this.selectedMember.type === AccountType.ADMIN
              ? PermissionsActiveDialog.MEMBERS
              : PermissionsActiveDialog.PERMISSIONS,
          mode: "edit"
        });
      }
    },

    async deleteMember(member) {
      this.loading = true;

      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.PERMISSIONS_USER_REMOVE,
        removed_user_id: member ? member.id : null
      });

      await serviceHandler(
        CompanyService.deleteMember(member.id, this.company.id)
      );
      await this.refresh();
      DialogHelper.showDialog(
        AlertDialog,
        {
          text: this.$t("message.permissions.accountDeleted"),
          buttonText: this.$t("message.common.ok").toUpperCase()
        },
        []
      );
      this.loading = false;
    },

    async previewMember(memberId) {
      await this.setMember(memberId);
      if (this.selectedMember && this.selectedMember.id === memberId) {
        this.$store.dispatch("permissions/setActiveDialog", {
          type: PermissionsActiveDialog.PERMISSIONS,
          mode: "preview"
        });
      }
    },

    getFacilitiesNumberLabel(count) {
      return pluralCount("message.pluralForms.facility", count);
    },

    getFacilitiesGroupNumberLabel(groupsCount, facilitiesCount) {
      return `${pluralCount(
        "message.pluralForms.group",
        groupsCount
      )} / ${this.getFacilitiesNumberLabel(facilitiesCount)}`;
    },

    getCellBorderStyle(value) {
      let background = APP_COLORS.LIGHT_3;

      if (value === 1) {
        background = APP_COLORS.ACCENT;
      } else if (value === 2) {
        background = APP_COLORS.PRIMARY;
      }

      return { background };
    },

    onPagination({ page, perPage }) {
      this.page = page;
      this.perPage = perPage;
      this.getMembers();
    },
    async sendInvitation(id) {
      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.PERMISSIONS_USER_RESEND_INVITATION,
        invited_user_id: id || null
      });

      await serviceHandler(usersService.resendInvitation(id));

      DialogHelper.showDialog(
        AlertDialog,
        {
          text: this.$t("message.permissions.invitationSent"),
          buttonText: this.$t("message.common.ok").toUpperCase()
        },
        []
      );
    },

    refresh() {
      this.page = 1;
      this.getMembers();
    }
  }
};
</script>

<style lang="scss" scoped>
.actions {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.type-cell {
  display: flex;
  flex-direction: row;
  align-items: center;
}

.cell-border-left {
  width: 3px;
  height: 53px;
  border-radius: 8px;
  margin-right: $spacing * 1.5;
}

.muted {
  line-height: 19px;
  opacity: 0.7;
}

.resend-btn {
  margin-left: $spacing * 2.5;
}

.permissions-label {
  line-height: $spacing * 2.5;
}

.see-permissions-btn {
  margin-left: -$spacing;
}
</style>

<style lang="scss">
.members-list.theme--light.v-data-table {
  color: $text;
  font-weight: 500;
  @include font-size(14px);
  line-height: 17px;

  thead tr th {
    font-weight: normal;
    @include font-size(12px);
    line-height: 15px;
    color: rgba($text, 0.6);

    &:first-of-type {
      padding-left: 0;
    }

    &:nth-of-type(2) {
      max-width: 320px;
    }
  }

  thead tr:last-child th {
    border-bottom: 0;
    vertical-align: bottom;
  }

  td {
    height: 90px;

    &:first-of-type {
      padding-left: 0;
    }

    &:nth-of-type(2) {
      max-width: 320px;
    }
  }

  .link-btn {
    padding: 0;
    height: unset;
  }

  .link-btn .v-btn__content {
    color: $text;
    font-weight: 600;
  }
}

.btn-action-menu.v-menu__content {
  box-shadow: none;
  border-radius: $container-border-radius;
  filter: $shadow-filter;
  margin-left: $spacing * -6;
}

.owner-btn {
  margin-left: $spacing * 2;

  .v-btn__content {
    color: $text !important;
  }
}

.no-members {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  height: 500px;
  white-space: pre-line;

  h3 {
    text-align: center;
    max-width: 600px;
    margin-bottom: $spacing * 7;
  }
}

.table-container,
.no-members {
  position: relative;
}
</style>
